import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AppProvider, useApp } from "@/context/AppContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import { Loading } from "@/components/common";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Players from "@/pages/Players";
import PlayerDetail from "@/pages/PlayerDetail";
import ImportPlayers from "@/pages/ImportPlayers";
import Teams from "@/pages/Teams";
import Training from "@/pages/Training";
import Matches from "@/pages/Matches";
import Tournaments from "@/pages/Tournaments";
import Finance from "@/pages/Finance";
import Inventory from "@/pages/Inventory";
import Documents from "@/pages/Documents";
import AIAssistant from "@/pages/AIAssistant";
import Notifications from "@/pages/Notifications";
import Users from "@/pages/Users";
import Settings from "@/pages/Settings";
import Trash from "@/pages/Trash";
import Reports from "@/pages/Reports";
import People from "@/pages/People";
import AdminParents from "@/pages/AdminParents";
import ParentPortal from "@/pages/ParentPortal";
import CoachPortal from "@/pages/CoachPortal";
import AICommandCenter from "@/pages/AICommandCenter";
import ParentLogin from "@/pages/ParentLogin";
import ParentDashboard from "@/pages/ParentDashboard";
import "@/App.css";

function Protected({ children }) {
  const { user } = useApp();
  if (user === undefined) return <div className="h-screen flex items-center justify-center"><Loading /></div>;
  if (user === null) return <Navigate to="/login" replace />;
  if (user.role === "parent") return <Navigate to="/parent/dashboard" replace />;
  return <Layout>{children}</Layout>;
}

function ParentOnly({ children }) {
  const { user } = useApp();
  if (user === undefined) return <div className="h-screen flex items-center justify-center"><Loading /></div>;
  if (user === null) return <Navigate to="/parent/login" replace />;
  if (user.role !== "parent") return <Navigate to="/" replace />;
  return children;
}

function Shell() {
  const { user } = useApp();
  const home = user ? (user.role === "parent" ? "/parent/dashboard" : "/") : "/login";
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to={home} replace /> : <Login />} />
      <Route path="/parent/login" element={<ParentLogin />} />
      <Route path="/parent/dashboard" element={<ParentOnly><ParentDashboard /></ParentOnly>} />
      <Route path="/" element={<Protected><Dashboard /></Protected>} />
      <Route path="/players" element={<Protected><Players /></Protected>} />
      <Route path="/players/import" element={<Protected><ImportPlayers /></Protected>} />
      <Route path="/players/:id" element={<Protected><PlayerDetail /></Protected>} />
      <Route path="/teams" element={<Protected><Teams /></Protected>} />
      <Route path="/training" element={<Protected><Training /></Protected>} />
      <Route path="/matches" element={<Protected><Matches /></Protected>} />
      <Route path="/tournaments" element={<Protected><Tournaments /></Protected>} />
      <Route path="/finance" element={<Protected><Finance /></Protected>} />
      <Route path="/inventory" element={<Protected><Inventory /></Protected>} />
      <Route path="/documents" element={<Protected><Documents /></Protected>} />
      <Route path="/ai" element={<Protected><AIAssistant /></Protected>} />
      <Route path="/ai-command" element={<Protected><AICommandCenter /></Protected>} />
      <Route path="/portal/coach" element={<Protected><CoachPortal /></Protected>} />
      <Route path="/portal/parent" element={<Protected><ParentPortal /></Protected>} />
      <Route path="/parents" element={<Protected><AdminParents /></Protected>} />
      <Route path="/coaches" element={<Protected><People role="coach" /></Protected>} />
      <Route path="/reports" element={<Protected><Reports /></Protected>} />
      <Route path="/notifications" element={<Protected><Notifications /></Protected>} />
      <Route path="/users" element={<Protected><Users /></Protected>} />
      <Route path="/settings" element={<Protected><Settings /></Protected>} />
      <Route path="/trash" element={<Protected><Trash /></Protected>} />
      <Route path="*" element={<Navigate to={home} replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <Shell />
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    </AppProvider>
  );
}
